import { createFileRoute, Link, Outlet, redirect } from "@tanstack/react-router";
import { NuqsAdapter } from "nuqs/adapters/react";
import { useTranslation } from "react-i18next";
import z from "zod";
import { Container } from "@/components/admin/Container";
import { Header } from "@/components/admin/Header";
import { AdminLayout } from "@/components/admin/Layout";
import { Searchbar } from "@/components/admin/Searchbar";
import { ErrorFallback } from "@/components/fallbacks";
import { Button, buttonVariants } from "@/components/ui/button";
import { getActiveBranchId } from "@/hooks/use-active-branch";
import { useCompleteProfile } from "@/hooks/use-complete-profile";
import { useValidateCurrentDevice } from "@/hooks/use-validate-current-device";
import { resolveBranchOwner, resolveSubscriptionDoctorId } from "@/lib/permissions";
import { pb } from "@/lib/pocketbase";
import { branchesQueries } from "@/modules/branches/queries";
import { invitationsQueries } from "@/modules/invitations/queries";
import { useCheckInvitation } from "@/modules/invitations/use-check-invitation";
import { getCurrentSubscriptionQuery } from "@/modules/settings/queries";

const validateSearch = z.object({
	invitation_token: z.string().optional(),
});

export const Route = createFileRoute("/dashboard")({
	validateSearch,
	beforeLoad: async ({ context, search }) => {
		const queryClient = context.queryClient;

		if (!pb.authStore.isValid) {
			queryClient.clear();
			throw redirect({
				to: "/auth/login",
				search: { redirectTo: window.location.pathname, ...search },
			});
		}

		if (!pb.authStore.record?.["verified"]) {
			let phoneNumber = "";

			if (typeof pb.authStore.record?.["phoneNumber"] === "string") {
				phoneNumber = pb.authStore.record["phoneNumber"];
			}

			throw redirect({
				to: "/auth/send-verification",
				search: { phoneNumber },
			});
		}

		const branches = await queryClient.ensureQueryData(branchesQueries.fullList());
		const activeBranchId = getActiveBranchId();
		const sessionDoctorId = pb.authStore.record?.id;
		const { ownerDoctorId, isGuest } = resolveBranchOwner({
			branches,
			activeBranchId,
			sessionDoctorId,
		});

		const subscriptionDoctorId = resolveSubscriptionDoctorId({
			ownerDoctorId,
			isGuest,
			sessionDoctorId,
		});

		const prefetchPromises = [
			queryClient.ensureQueryData(invitationsQueries.byToken(search.invitation_token)),
			queryClient.ensureQueryData(getCurrentSubscriptionQuery({ doctorId: subscriptionDoctorId })),
		];

		await Promise.allSettled(prefetchPromises);
	},
	component: RouteComponent,
	pendingComponent: () => (
		<AdminLayout>
			<div className="flex h-full w-full items-center justify-center">
				<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
			</div>
		</AdminLayout>
	),
	notFoundComponent: function Component() {
		const { t } = useTranslation();

		return (
			<AdminLayout>
				<Header>
					<div className="ml-auto flex items-center space-x-4">
						<Searchbar />
					</div>
				</Header>
				<Container className="mt-40">
					<ErrorFallback title={t(($) => $.common.status.pageNotFound.title)}>
						<p className="max-w-md text-center text-muted-foreground">
							{t(($) => $.common.status.pageNotFound.message)}
						</p>

						<Link
							className={buttonVariants({
								className: "mt-4",
							})}
							to={"/dashboard"}
						>
							{t(($) => $.common.status.pageNotFound.action)}
						</Link>
					</ErrorFallback>
				</Container>
			</AdminLayout>
		);
	},
	errorComponent: function Component({ error, reset }) {
		const { t } = useTranslation();

		return (
			<AdminLayout>
				<Header>
					<div className="ml-auto flex items-center space-x-4">
						<Searchbar />
					</div>
				</Header>
				<Container className="mt-40">
					<ErrorFallback title={t(($) => $.common.status.pageError.title)}>
						<p className="max-w-md text-center text-muted-foreground">
							{error.message || t(($) => $.common.status.pageError.message)}
						</p>

						<Button className="mt-4" onClick={() => reset()}>
							{t(($) => $.common.status.pageError.action)}
						</Button>
					</ErrorFallback>
				</Container>
			</AdminLayout>
		);
	},
});

function RouteComponent() {
	useValidateCurrentDevice();
	useCompleteProfile();
	useCheckInvitation();

	return (
		<AdminLayout>
			<NuqsAdapter>
				<Outlet />
			</NuqsAdapter>
		</AdminLayout>
	);
}
